import { useLiveQuery } from 'dexie-react-hooks'
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react'
import { Check, Clock, FileText, HardDrive, HardHat, RefreshCw, TriangleAlert, Users, WifiOff } from 'lucide-react'
import { MotionConfig, motion } from 'motion/react'
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { registerSW } from 'virtual:pwa-register'
import { demarrerSynchroAuto } from './db/sync'
import { useCompte, useRole, verifierSession, type CompteLocal } from './lib/compte'
import { demanderStockagePersistant, estInstallee, estIos } from './lib/plateforme'
import { getBase } from './outbox/base'
import { compterEnAttente, compterRejetees } from './outbox/depot'
import Accueil from './pages/Accueil'
import Bibliotheque from './pages/Bibliotheque'
import EditionDevis from './pages/EditionDevis'
import Clients from './pages/Clients'
import Devis from './pages/Devis'
import FicheClient from './pages/FicheClient'
import Impayes from './pages/Impayes'
import NouveauDevis from './pages/NouveauDevis'
import Plus from './pages/Plus'
import { BarreLaterale, BarreOnglets } from './ui/onglets'
import { Colonne, DetailVide, MaitreDetail } from './ui/maitre-detail'
import { CHEMINS_LEGAUX } from './pages/chemins-legaux'
import { FournisseurSync, useSync } from './ui/synchro'
import { EcranVerrou, getVerrou, useEtatVerrou } from './ui/verrou'

// Écrans occasionnels, chargés à la demande (précachés pour le hors ligne)
const EcranConnexion = lazy(() => import('./ui/ecran-connexion'))
const Donnees = lazy(() => import('./pages/Donnees'))
const Compte = lazy(() => import('./pages/Compte'))
const Reglages = lazy(() => import('./pages/Reglages'))
const Situations = lazy(() => import('./pages/Situations'))
const EditionSituation = lazy(() => import('./pages/EditionSituation'))
const Chantiers = lazy(() => import('./pages/Chantiers'))
const Chantier = lazy(() => import('./pages/Chantier'))
const Heures = lazy(() => import('./pages/Heures'))
const Journal = lazy(() => import('./pages/Journal'))
const FeuilleHeures = lazy(() => import('./pages/FeuilleHeures'))
const Rapprochement = lazy(() => import('./pages/Rapprochement'))
const Planning = lazy(() => import('./pages/Planning'))
const Frise = lazy(() => import('./pages/Frise'))

// Écrans à liste et détail : la transition ne remonte pas la liste quand on ouvre une ligne
const SECTIONS_LISTE = ['/devis', '/clients', '/chantiers']

function Chargement({ children }: { children: ReactNode }) {
  return <Suspense fallback={<main className="flex-1" />}>{children}</Suspense>
}

function etatReseau(online: boolean, compte: CompteLocal | null, enAttente: number) {
  if (compte && !compte.connecte) return { Icone: TriangleAlert, couleur: 'text-red-300', libelle: 'Non connecté : reconnectez-vous' }
  if (!compte) return { Icone: HardDrive, couleur: 'text-slate-300', libelle: 'Sur cet appareil' }
  if (!online) return { Icone: WifiOff, couleur: 'text-slate-300', libelle: enAttente > 0 ? `Hors ligne · ${enAttente} en attente` : 'Hors ligne' }
  if (enAttente > 0) {
    return { Icone: Clock, couleur: 'text-slate-300', libelle: `${enAttente} modification${enAttente > 1 ? 's' : ''} en attente` }
  }
  return { Icone: Check, couleur: 'text-green-400', libelle: 'Synchronisé' }
}

function Coquille() {
  const [online, setOnline] = useState(navigator.onLine)
  const [needRefresh, setNeedRefresh] = useState(false)
  // Données locales (Dexie sur IndexedDB), réactives et partagées entre onglets
  const base = getBase()
  const enAttente = useLiveQuery(() => compterEnAttente(base), [], 0)
  const rejetees = useLiveQuery(() => compterRejetees(base), [], 0)
  const { message: messageSync, enCours, lancer } = useSync()
  const updateSW = useRef<(reload?: boolean) => Promise<void>>(undefined)
  const inviterInstallation = estIos() && !estInstallee()
  const compte = useCompte()
  const role = useRole()
  const reseau = etatReseau(online, compte, enAttente)
  const { pathname } = useLocation()

  // Service Worker : mise à jour proposée, jamais forcée
  useEffect(() => {
    updateSW.current = registerSW({
      onNeedRefresh: () => setNeedRefresh(true),
    })
  }, [])

  // Évite que le navigateur purge la base locale
  useEffect(() => {
    void demanderStockagePersistant()
  }, [])

  // Réplication vers l'API : au démarrage, au retour du réseau et au premier plan (compte connecté)
  const connecte = compte?.connecte ?? false
  useEffect(() => (connecte ? demarrerSynchroAuto() : undefined), [connecte])
  useEffect(() => void verifierSession(), [])

  // Chiffrement local activé : verrouillage après inactivité et au retour d'arrière-plan
  useEffect(() => getVerrou().surveiller(), [])

  // État réseau
  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col bg-brume text-nuit lg:h-dvh lg:max-w-none">
      <header className="flex items-center justify-between gap-2 bg-nuit p-4 text-white lg:px-6">
        <Link to="/" className="flex items-center gap-2.5">
          <svg viewBox="0 0 28 44" className="h-10 w-6 shrink-0" aria-hidden>
            <line x1="14" y1="0" x2="14" y2="14" stroke="#F5A800" strokeWidth="3" />
            <polygon points="14,14 24,26 14,44 4,26" fill="#F5A800" />
          </svg>
          <span>
            <span className="block text-2xl leading-tight font-bold">Fil-à-Plomb</span>
            <span className="flex items-center gap-1.5 text-xs leading-tight text-slate-300">
              <span aria-hidden className={`size-2.5 rounded-full ${online ? 'bg-green-400' : 'bg-red-500'}`} />
              {online ? 'En ligne' : 'Hors ligne'}
            </span>
          </span>
        </Link>
        {/* Indicateur réseau : synchronisé, en attente (avec le nombre), hors ligne. Un appui lance la synchro, sauf sans compte connecté (il mène au compte) */}
        <div role="status">
          {compte?.connecte ? (
            <button type="button" onClick={() => void lancer()} disabled={enCours} className="flex min-h-12 max-w-36 items-center gap-2 text-left text-sm text-slate-200">
              {enCours ? <RefreshCw className="size-4 shrink-0 animate-spin" aria-hidden /> : <reseau.Icone className={`size-4 shrink-0 ${reseau.couleur}`} aria-hidden />}
              {enCours ? 'Synchronisation…' : reseau.libelle}
            </button>
          ) : (
            <Link to="/compte" className="flex min-h-12 max-w-36 items-center gap-2 text-left text-sm text-slate-200">
              <reseau.Icone className={`size-4 shrink-0 ${reseau.couleur}`} aria-hidden />
              {reseau.libelle}
            </Link>
          )}
        </div>
      </header>

      <div className="flex flex-col gap-3 px-4 empty:hidden">
        {inviterInstallation && (
          <p className="rounded border-2 border-nuit bg-slate-100 p-3 text-sm">
            Pour garder vos données sur cet iPhone, installez l'app : bouton Partager, puis « Sur l'écran
            d'accueil ».
          </p>
        )}
        {needRefresh && (
          <button
            className="min-h-12 rounded-xl bg-primaire px-4 font-semibold text-nuit"
            onClick={() => updateSW.current?.(true)}
          >
            Nouvelle version disponible : mettre à jour
          </button>
        )}
        {messageSync && <p className="rounded bg-slate-100 p-3 text-sm">{messageSync}</p>}
        {compte?.abonnementRefuse && (
          <p role="status" className="rounded border-2 border-nuit bg-slate-100 p-3 text-sm text-nuit">
            Abonnement de votre entreprise expiré : vos données restent consultables, mais vos modifications ne sont plus
            envoyées. Elles sont conservées sur cet appareil. Contactez l'administrateur de votre abonnement.
          </p>
        )}
        {rejetees > 0 && (
          <p className="rounded border-2 border-red-700 bg-red-50 p-3 text-sm text-red-900">
            {rejetees} modification{rejetees > 1 ? 's' : ''} refusée{rejetees > 1 ? 's' : ''} par le serveur, conservée
            {rejetees > 1 ? 's' : ''} sur cet appareil.
          </p>
        )}
      </div>

      {/* Chaque page porte sa barre d'actions (BarreActions) en bas d'écran. Sur ordinateur : menu latéral, et liste + détail côte à côte pour devis, clients et chantiers */}
      <div className="flex flex-1 flex-col lg:min-h-0 lg:flex-row">
        <BarreLaterale />
        <div className="flex min-w-0 flex-1 flex-col lg:min-h-0 lg:overflow-y-auto">
          <motion.div
            key={SECTIONS_LISTE.find((r) => pathname === r || pathname.startsWith(`${r}/`)) ?? pathname}
            className="flex flex-1 flex-col lg:min-h-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          >
            {role === 'ouvrier' ? (
              // Ouvrier : ses chantiers, le planning de ses tâches, ses heures, le journal et son compte (le serveur n'envoie rien d'autre)
              <Routes>
                <Route path="/" element={<Navigate to="/chantiers" replace />} />
                <Route path="/chantiers" element={<MaitreDetail racine="/chantiers" liste={<Chargement><Chantiers /></Chargement>} />}>
                  <Route index element={<DetailVide Icone={HardHat} texte="Sélectionnez un chantier pour le consulter." />} />
                  <Route path=":id" element={<Chantier />} />
                  <Route path=":id/heures" element={<Heures />} />
                  <Route path=":id/journal" element={<Journal />} />
                </Route>
                <Route element={<Colonne />}>
                  {[
                    ['/compte', <Compte />],
                    ['/planning', <Planning />],
                    ['/heures', <FeuilleHeures />],
                  ].map(([chemin, page]) => (
                    <Route key={chemin as string} path={chemin as string} element={<Chargement>{page}</Chargement>} />
                  ))}
                </Route>
                <Route path="*" element={<Navigate to="/chantiers" replace />} />
              </Routes>
            ) : (
              <Routes>
                <Route path="/devis" element={<MaitreDetail racine="/devis" liste={<Devis />} />}>
                  <Route index element={<DetailVide Icone={FileText} texte="Sélectionnez un devis pour le consulter, ou créez-en un nouveau." />} />
                  <Route path="nouveau" element={<NouveauDevis />} />
                  <Route path=":id" element={<EditionDevis />} />
                  <Route path=":id/situations" element={<Situations />} />
                </Route>
                <Route path="/clients" element={<MaitreDetail racine="/clients" liste={<Clients />} />}>
                  <Route index element={<DetailVide Icone={Users} texte="Sélectionnez un client pour ouvrir sa fiche." />} />
                  <Route path=":id" element={<FicheClient />} />
                </Route>
                <Route path="/chantiers" element={<MaitreDetail racine="/chantiers" liste={<Chargement><Chantiers /></Chargement>} />}>
                  <Route index element={<DetailVide Icone={HardHat} texte="Sélectionnez un chantier pour le consulter." />} />
                  <Route path=":id" element={<Chantier />} />
                  <Route path=":id/heures" element={<Heures />} />
                  <Route path=":id/journal" element={<Journal />} />
                </Route>
                <Route element={<Colonne />}>
                  <Route path="/" element={<Accueil />} />
                  <Route path="/plus" element={<Plus />} />
                  <Route path="/impayes" element={<Impayes />} />
                  <Route path="/rapprochement" element={<Chargement><Rapprochement /></Chargement>} />
                  <Route path="/bibliotheque" element={<Bibliotheque />} />
                  <Route path="/donnees" element={<Chargement><Donnees /></Chargement>} />
                  <Route path="/compte" element={<Chargement><Compte /></Chargement>} />
                  <Route path="/reglages" element={role === 'membre' ? <Navigate to="/" replace /> : <Chargement><Reglages /></Chargement>} />
                  <Route path="/situations/:id" element={<Chargement><EditionSituation /></Chargement>} />
                  <Route path="/heures" element={<Chargement><FeuilleHeures /></Chargement>} />
                  <Route path="/planning" element={<Chargement><Planning /></Chargement>} />
                  <Route path="/gantt" element={<Chargement><Frise /></Chargement>} />
                  <Route path="*" element={<Accueil />} />
                </Route>
              </Routes>
            )}
          </motion.div>
        </div>
      </div>

      <BarreOnglets />
    </div>
  )
}

function AppArtisan() {
  // Données chiffrées verrouillées : l'app n'est pas montée (aucune lecture possible)
  const verrou = useEtatVerrou()
  const compte = useCompte()
  if (verrou === 'chargement') return null
  if (verrou === 'verrouille') return <EcranVerrou />
  // Premier lancement : pas d'accès sans compte. Une session expirée garde `compte` (connecte: false) : l'app reste utilisable hors ligne.
  if (compte === null) {
    return (
      <Suspense fallback={null}>
        <EcranConnexion />
      </Suspense>
    )
  }
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <FournisseurSync>
          <Coquille />
        </FournisseurSync>
      </BrowserRouter>
    </MotionConfig>
  )
}

const SignatureEnLigne = lazy(() => import('./pages/SignatureEnLigne'))
const PageLegale = lazy(() => import('./pages/PageLegale'))
const JETON_SIGNATURE = /^\/signer\/([A-Za-z0-9_-]+)\/?$/

/** `/signer/<jeton>` est la page du client et les pages légales sont publiques (sans compte, sans base locale) ; tout le reste est l'application de l'artisan. */
export default function App() {
  const legale = CHEMINS_LEGAUX[location.pathname.replace(/\/$/, '')]
  if (legale) {
    return (
      <Suspense fallback={null}>
        <PageLegale type={legale} />
      </Suspense>
    )
  }
  const jeton = JETON_SIGNATURE.exec(location.pathname)?.[1]
  if (jeton) {
    return (
      <Suspense fallback={null}>
        <SignatureEnLigne jeton={jeton} />
      </Suspense>
    )
  }
  return <AppArtisan />
}
